import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Archive, FolderOpen, MoreHorizontal, Pause, Play } from "lucide-react";

import { archiveProject, getAllTasks, getClients, getProjects, pauseProject, resumeProject } from "@/lib/api";
import { canCreatePortfolio } from "@/lib/org";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";

const statusBadge = {
  healthy: "chip chip-ok",
  watch: "chip chip-warn",
  at_risk: "chip chip-risk",
  critical: "chip chip-crit",
};

const avatarTone = ["avatar-a", "avatar-b", "avatar-c", "avatar-d", "avatar-e"];

const healthBand = (score) => {
  if (score >= 80) return "healthy";
  if (score >= 65) return "watch";
  if (score >= 50) return "at_risk";
  return "critical";
};

const initials = (value = "") => {
  const clean = value.trim();
  if (!clean) return "NA";
  const chunks = clean.split(" ");
  if (chunks.length === 1) return chunks[0].slice(0, 2).toUpperCase();
  return `${chunks[0][0]}${chunks[chunks.length - 1][0]}`.toUpperCase();
};

const statusMeta = {
  active: { title: "Active", dot: "#16A34A", headerClass: "bg-emerald-50/70", emptyText: "No active projects" },
  paused: { title: "Paused", dot: "#D97706", headerClass: "bg-amber-50/80", emptyText: "No paused projects" },
  completed: { title: "Completed", dot: "#0284C7", headerClass: "bg-sky-50/80", emptyText: "No completed projects" },
  archived: { title: "Archived", dot: "#6B7280", headerClass: "bg-slate-100/70", emptyText: "No archived projects" },
};

const pauseReasons = [
  "Client requested hold",
  "Awaiting client inputs / assets",
  "Payment overdue",
  "Scope review in progress",
  "Internal team capacity",
  "Other",
];

const archiveReasons = [
  "Project completed",
  "Client churned",
  "Scope cancelled",
  "Non-payment",
  "Merged into another project",
];

export default function ProjectsPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [clientById, setClientById] = useState(new Map());
  const [isLoading, setIsLoading] = useState(true);

  const [pauseModal, setPauseModal] = useState({ open: false, project: null });
  const [archiveModal, setArchiveModal] = useState({ open: false, project: null });
  const [pauseForm, setPauseForm] = useState({ reason: "", notes: "", resume_expected: "" });
  const [archiveForm, setArchiveForm] = useState({ reason: "", confirm_name: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canCreateProject = canCreatePortfolio(user);

  const loadProjects = async () => {
    setIsLoading(true);
    try {
      const [projectRows, taskRows, clientRows] = await Promise.all([
        getProjects(),
        getAllTasks({
          assigned_scope: ["super_admin", "project_admin", "account_manager"].includes(user.role) ? "all" : "assigned_to_you",
          limit: 1000,
        }),
        getClients(),
      ]);
      setProjects(projectRows);
      setTasks(taskRows);
      setClientById(new Map(clientRows.map((client) => [client.id, client])));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const taskMap = useMemo(() => {
    const map = new Map();
    tasks.forEach((task) => {
      const item = map.get(task.project_id) || { pending: 0, done: 0, overdue: 0, team: [] };
      if (task.status === "completed") item.done += 1;
      else item.pending += 1;
      if (task.assigned_to && !item.team.includes(task.assigned_to)) item.team.push(task.assigned_to);
      const due = new Date(task.deadline);
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      if (task.status !== "completed" && !Number.isNaN(due.getTime()) && due < now) item.overdue += 1;
      map.set(task.project_id, item);
    });
    return map;
  }, [tasks]);

  const sortedProjects = useMemo(
    () => [...projects].sort((a, b) => Number(a.health_score || 0) - Number(b.health_score || 0)),
    [projects],
  );

  const projectsByStatus = useMemo(() => {
    const grouped = { active: [], paused: [], completed: [], archived: [] };
    sortedProjects.forEach((project) => {
      const status = String(project.status || "active").toLowerCase();
      if (grouped[status]) grouped[status].push(project);
    });
    return grouped;
  }, [sortedProjects]);

  const leadCount = useMemo(
    () => sortedProjects.filter((project) => project.project_admin_email === user.email).length,
    [sortedProjects, user.email],
  );

  const activeRetainers = useMemo(
    () => sortedProjects.filter((project) => String(project.status || "active").toLowerCase() === "active" && (project.project_type || "retainer") === "retainer").length,
    [sortedProjects],
  );

  const isProjectOwner = (project) => {
    const email = String(user.email || "").toLowerCase();
    return [String(project.manager_email || "").toLowerCase(), String(project.project_admin_email || "").toLowerCase()].includes(email);
  };

  const isPodOwnerForProject = (project) => {
    const projectPodName = String(project.pod_name || "").trim().toLowerCase();
    const userPodName = String(user.pod_name || "").trim().toLowerCase();
    const isPodOwner = Boolean(user.is_pod_owner) || Number(user.org_level || 0) === 2;
    return Boolean(isPodOwner && projectPodName && userPodName && projectPodName === userPodName);
  };

  const canPauseOrResumeProject = (project) => {
    const status = String(project.status || "active").toLowerCase();
    if (!status) return false;
    if (user.role === "super_admin") return true;
    if (isProjectOwner(project)) return true;
    return isPodOwnerForProject(project);
  };

  const canArchiveProject = (project) => {
    const status = String(project.status || "active").toLowerCase();
    if (status === "archived") return false;
    if (user.role === "super_admin") return true;
    if (isProjectOwner(project)) return true;
    return isPodOwnerForProject(project);
  };

  const openPauseModal = (project) => {
    setPauseForm({ reason: "", notes: "", resume_expected: "" });
    setPauseModal({ open: true, project });
  };

  const openArchiveModal = (project) => {
    setArchiveForm({ reason: "", confirm_name: "" });
    setArchiveModal({ open: true, project });
  };

  const handleResumeProject = async (project) => {
    try {
      await resumeProject(project.id, { reason: "Resumed from projects overview" });
      toast.success("Project resumed");
      await loadProjects();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const confirmPauseProject = async () => {
    const selectedProject = pauseModal.project;
    if (!selectedProject) return;
    if (!pauseForm.reason) {
      toast.error("Please select a pause reason");
      return;
    }
    setIsSubmitting(true);
    try {
      const mergedReason = pauseForm.notes ? `${pauseForm.reason} — ${pauseForm.notes}` : pauseForm.reason;
      await pauseProject(selectedProject.id, {
        reason: mergedReason,
        resume_expected: pauseForm.resume_expected || null,
      });
      toast.success("Project paused");
      setPauseModal({ open: false, project: null });
      await loadProjects();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const archiveConfirmMatched = useMemo(() => {
    const project = archiveModal.project;
    if (!project) return false;
    const expected = String(project.client_name || "").trim().toLowerCase();
    return String(archiveForm.confirm_name || "").trim().toLowerCase() === expected && Boolean(archiveForm.reason);
  }, [archiveForm.confirm_name, archiveForm.reason, archiveModal.project]);

  const confirmArchiveProject = async () => {
    const selectedProject = archiveModal.project;
    if (!selectedProject || !archiveConfirmMatched) return;
    setIsSubmitting(true);
    try {
      await archiveProject(selectedProject.id, {
        reason: archiveForm.reason,
        confirm_name: archiveForm.confirm_name,
      });
      toast.success("Project archived");
      setArchiveModal({ open: false, project: null });
      await loadProjects();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppShell user={user} onLogout={onLogout} topbarSearchPlaceholder="Search tasks, projects...">
      <section className="space-y-4" data-testid="projects-page-root">
        <div className="page-header" data-testid="projects-header-card">
          <div>
            <h1 className="page-title" data-testid="projects-title">Projects</h1>
            <p className="page-sub" data-testid="projects-subtitle">
              {activeRetainers} active retainers · {leadCount} you lead · {Math.max(0, sortedProjects.length - leadCount)} where you collaborate
            </p>
          </div>
          {canCreateProject ? (
            <Button onClick={() => navigate("/clients")} style={{ height: 48, paddingInline: 16 }} data-testid="projects-new-project-button">+ New Project</Button>
          ) : null}
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="projects-loading-state">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={`project-skeleton-${index}`} className="card p-5" data-testid={`projects-loading-skeleton-${index}`}>
                <div className="skeleton-shimmer" style={{ height: 14, width: "58%", marginBottom: 10 }} />
                <div className="skeleton-shimmer" style={{ height: 12, width: "40%", marginBottom: 16 }} />
                <div className="grid grid-cols-3 gap-2" style={{ marginBottom: 14 }}>
                  <div className="skeleton-shimmer" style={{ height: 48, borderRadius: 8 }} />
                  <div className="skeleton-shimmer" style={{ height: 48, borderRadius: 8 }} />
                  <div className="skeleton-shimmer" style={{ height: 48, borderRadius: 8 }} />
                </div>
                <div className="skeleton-shimmer" style={{ height: 8, borderRadius: 999 }} />
              </div>
            ))}
          </div>
        ) : (
          <Accordion type="multiple" defaultValue={["active"]} className="space-y-3" data-testid="projects-status-accordions">
            {(["active", "paused", "archived", "completed"]).map((statusKey) => {
              const rows = projectsByStatus[statusKey] || [];
              const meta = statusMeta[statusKey];
              return (
                <AccordionItem key={statusKey} value={statusKey} className="card border border-slate-200" data-testid={`projects-accordion-${statusKey}`}>
                  <AccordionTrigger className={`px-4 py-3 hover:no-underline ${meta.headerClass}`} data-testid={`projects-accordion-trigger-${statusKey}`}>
                    <div className="flex items-center gap-2 text-left">
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: meta.dot }} />
                      <span style={{ fontWeight: 700 }}>{meta.title}</span>
                      <span className="chip chip-mute" data-testid={`projects-accordion-count-${statusKey}`}>{rows.length}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4 pt-3" data-testid={`projects-accordion-content-${statusKey}`}>
                    {rows.length === 0 ? (
                      <div className="jump-card p-4 text-sm text-slate-500" data-testid={`projects-accordion-empty-${statusKey}`}>{meta.emptyText}</div>
                    ) : (
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid={`projects-grid-${statusKey}`}>
                        {rows.map((project, index) => {
                          const stats = taskMap.get(project.id) || { pending: 0, done: 0, overdue: 0, team: [] };
                          const total = Math.max(1, stats.pending + stats.done);
                          const progress = Math.round((stats.done / total) * 100);
                          const healthScore = Number(project.health_score || 0);
                          const band = healthBand(healthScore);
                          const teamMembers = (stats.team.length ? stats.team : [project.project_admin_email]).filter(Boolean).slice(0, 2);
                          const client = clientById.get(project.client_id);
                          const subtitleLeft = client?.industry || project.service_type;
                          const subtitleRight = (project.project_type || "retainer") === "retainer" ? "Retainer" : "Fixed";
                          const projectStatus = String(project.status || "active").toLowerCase();
                          const isArchived = projectStatus === "archived";
                          const isPaused = projectStatus === "paused";
                          const canPauseResume = canPauseOrResumeProject(project);
                          const canArchive = canArchiveProject(project);

                          return (
                            <article
                              key={project.id}
                              className="card group relative transition hover:-translate-y-0.5"
                              style={{
                                opacity: isArchived ? 0.55 : 1,
                                background: isPaused ? "linear-gradient(180deg,#FFFBEB,#FFFFFF)" : "#fff",
                                borderColor: isPaused ? "#FDE68A" : undefined,
                                cursor: isArchived ? "default" : "pointer",
                              }}
                              onClick={() => {
                                if (!isArchived) navigate(`/project/${project.id}`);
                              }}
                              data-testid={`project-card-${project.id}`}
                            >
                              <div className="absolute right-3 top-3 z-10" data-testid={`projects-card-overflow-wrap-${project.id}`}>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type="button"
                                      className="rounded-md border border-slate-200 bg-white/95 p-1.5 text-slate-600 shadow-sm opacity-0 transition-all group-hover:opacity-100 hover:bg-slate-50"
                                      onClick={(event) => event.stopPropagation()}
                                      data-testid={`projects-card-overflow-trigger-${project.id}`}
                                      aria-label="Project actions"
                                    >
                                      <MoreHorizontal className="h-4 w-4" />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" sideOffset={8} data-testid={`projects-card-overflow-menu-${project.id}`}>
                                    <DropdownMenuLabel>Project actions</DropdownMenuLabel>
                                    {projectStatus === "active" && canPauseResume ? (
                                      <DropdownMenuItem
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          openPauseModal(project);
                                        }}
                                        className="text-amber-700"
                                        data-testid={`projects-card-overflow-pause-${project.id}`}
                                      >
                                        <Pause className="h-4 w-4" />
                                        Pause project
                                      </DropdownMenuItem>
                                    ) : null}
                                    {projectStatus === "paused" && canPauseResume ? (
                                      <DropdownMenuItem
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleResumeProject(project);
                                        }}
                                        className="text-emerald-700"
                                        data-testid={`projects-card-overflow-resume-${project.id}`}
                                      >
                                        <Play className="h-4 w-4" />
                                        Resume project
                                      </DropdownMenuItem>
                                    ) : null}
                                    {projectStatus === "archived" && canPauseResume ? (
                                      <DropdownMenuItem
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleResumeProject(project);
                                        }}
                                        className="text-emerald-700"
                                        data-testid={`projects-card-overflow-restore-${project.id}`}
                                      >
                                        <Play className="h-4 w-4" />
                                        Restore project
                                      </DropdownMenuItem>
                                    ) : null}
                                    <DropdownMenuItem
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        navigate(`/project/${project.id}`);
                                      }}
                                      data-testid={`projects-card-overflow-open-${project.id}`}
                                    >
                                      <FolderOpen className="h-4 w-4" />
                                      Open project
                                    </DropdownMenuItem>
                                    {canArchive ? (
                                      <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            openArchiveModal(project);
                                          }}
                                          className="text-rose-700"
                                          data-testid={`projects-card-overflow-archive-${project.id}`}
                                        >
                                          <Archive className="h-4 w-4" />
                                          Archive project
                                        </DropdownMenuItem>
                                      </>
                                    ) : null}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>

                              <div style={{ padding: 20 }}>
                                <div className="mb-4 flex items-start justify-between gap-3 pr-8">
                                  <div className="flex items-center gap-2">
                                    <span className={`avatar avatar-md ${avatarTone[index % avatarTone.length]}`} data-testid={`project-card-avatar-${project.id}`}>
                                      {initials(project.client_name || project.name)}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                      <p className="truncate" style={{ fontWeight: 600, fontSize: 14 }} data-testid={`project-card-title-${project.id}`}>{project.client_name || project.name}</p>
                                      <p className="truncate" style={{ fontSize: 11.5, color: "var(--mute-500)" }} data-testid={`project-card-subtitle-${project.id}`}>{subtitleLeft} · {subtitleRight}</p>
                                      <div className="mt-1 flex flex-wrap items-center gap-1" data-testid={`project-card-tags-${project.id}`}>
                                        <span className="chip chip-mute" data-testid={`project-card-category-chip-${project.id}`}>{project.service_type || "Service"}</span>
                                        <span className={subtitleRight === "Fixed" ? "chip chip-violet" : "chip chip-brand"} data-testid={`project-card-billing-chip-${project.id}`}>{subtitleRight.toUpperCase()}</span>
                                      </div>
                                    </div>
                                  </div>
                                  <span className={statusBadge[band]} data-testid={`project-card-health-${project.id}`}>
                                    <span className={`dot ${band === "healthy" ? "dot-ok" : band === "watch" ? "dot-warn" : band === "at_risk" ? "dot-risk" : "dot-crit"}`} />
                                    {healthScore} {band === "at_risk" ? "At risk" : band.charAt(0).toUpperCase() + band.slice(1)}
                                  </span>
                                </div>

                                <div className="mb-3 flex items-center justify-between gap-2">
                                  <span className={`chip ${projectStatus === "active" ? "chip-ok" : projectStatus === "paused" ? "chip-warn" : projectStatus === "completed" ? "chip-brand" : "chip-crit"}`} data-testid={`project-card-status-${project.id}`}>
                                    {projectStatus}
                                  </span>
                                  {projectStatus === "archived" ? <span className="text-xs text-slate-500" data-testid={`project-card-archived-lock-${project.id}`}>Read only</span> : null}
                                </div>

                                <div className="mb-4 grid grid-cols-3 gap-2 text-center" data-testid={`project-card-metrics-${project.id}`}>
                                  <div style={{ padding: 8, background: "var(--paper-50)", borderRadius: 8 }}>
                                    <p style={{ fontSize: 11, color: "var(--mute-500)" }}>Pending</p>
                                    <p style={{ fontSize: 15, fontWeight: 600 }} data-testid={`project-card-pending-${project.id}`}>{stats.pending}</p>
                                  </div>
                                  <div style={{ padding: 8, background: "var(--paper-50)", borderRadius: 8 }}>
                                    <p style={{ fontSize: 11, color: "var(--mute-500)" }}>Done</p>
                                    <p style={{ fontSize: 15, fontWeight: 600 }} data-testid={`project-card-done-${project.id}`}>{stats.done}</p>
                                  </div>
                                  <div style={{ padding: 8, background: "var(--crit-50)", borderRadius: 8 }}>
                                    <p style={{ fontSize: 11, color: "var(--crit-700)" }}>Overdue</p>
                                    <p style={{ fontSize: 15, fontWeight: 600, color: "var(--crit-700)" }} data-testid={`project-card-overdue-${project.id}`}>{stats.overdue}</p>
                                  </div>
                                </div>

                                <div data-testid={`project-card-progress-${project.id}`}>
                                  <div className="mb-1 flex items-center justify-between" style={{ fontSize: 11.5, color: "var(--mute-500)" }}>
                                    <span>Progress</span>
                                    <span>{progress}%</span>
                                  </div>
                                  <div className="progress-bar"><div className="progress-fill" style={{ width: `${progress}%`, background: "var(--ink-900)", transition: "width .28s ease" }} /></div>
                                </div>

                                <div className="mt-4 flex items-center justify-between">
                                  <div className="flex" style={{ gap: 0 }} data-testid={`project-card-team-${project.id}`}>
                                    {teamMembers.map((member, memberIndex) => (
                                      <span key={`${project.id}-${member}`} className={`avatar avatar-sm ${avatarTone[memberIndex % avatarTone.length]}`} style={{ marginLeft: "-4px" }} data-testid={`project-card-team-avatar-${project.id}-${String(member).replace(/[@.]/g, "-")}`}>
                                        {initials(String(member))}
                                      </span>
                                    ))}
                                  </div>
                                  {!isArchived ? <p style={{ fontSize: 11.5, color: "var(--mute-500)" }} data-testid={`project-card-updated-${project.id}`}>Updated 2h ago</p> : null}
                                </div>
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}

        <Dialog open={pauseModal.open} onOpenChange={(open) => setPauseModal((prev) => ({ ...prev, open }))}>
          <DialogContent className="sm:max-w-[560px]" data-testid="projects-pause-modal">
            <DialogHeader>
              <DialogTitle data-testid="projects-pause-modal-title">Pause project</DialogTitle>
              <DialogDescription data-testid="projects-pause-modal-subtitle">{pauseModal.project?.name || "Project"}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[13px] leading-6 text-amber-900" data-testid="projects-pause-modal-impact-note">
                <strong>Pauses active delivery and reporting flow.</strong><br />
                Project is moved to Paused, removed from active totals, and can be resumed later.
              </div>
              <label className="field-label">Reason *</label>
              <select className="field-select" value={pauseForm.reason} onChange={(event) => setPauseForm((prev) => ({ ...prev, reason: event.target.value }))} data-testid="projects-pause-reason-select">
                <option value="">Select reason...</option>
                {pauseReasons.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
              <label className="field-label">Notes (optional)</label>
              <Textarea value={pauseForm.notes} onChange={(event) => setPauseForm((prev) => ({ ...prev, notes: event.target.value }))} data-testid="projects-pause-notes-input" />
              <label className="field-label">Expected resume date (optional)</label>
              <Input type="date" value={pauseForm.resume_expected} onChange={(event) => setPauseForm((prev) => ({ ...prev, resume_expected: event.target.value }))} data-testid="projects-pause-resume-date-input" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPauseModal({ open: false, project: null })} data-testid="projects-pause-cancel-button">Cancel</Button>
              <Button onClick={confirmPauseProject} disabled={isSubmitting} style={{ background: "#D97706", borderColor: "#D97706" }} data-testid="projects-pause-confirm-button">
                {isSubmitting ? "Pausing..." : "Pause project"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={archiveModal.open} onOpenChange={(open) => setArchiveModal((prev) => ({ ...prev, open }))}>
          <DialogContent className="sm:max-w-[560px]" data-testid="projects-archive-modal">
            <DialogHeader>
              <DialogTitle className="text-rose-700" data-testid="projects-archive-modal-title">Archive project</DialogTitle>
              <DialogDescription data-testid="projects-archive-modal-subtitle">{archiveModal.project?.name || "Project"}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-[13px] leading-6 text-rose-800" data-testid="projects-archive-modal-warning-note">
                <strong>This will move the project to Archived.</strong><br />
                Archived projects are removed from active views and remain read-only.
              </div>
              <label className="field-label">Reason *</label>
              <select className="field-select" value={archiveForm.reason} onChange={(event) => setArchiveForm((prev) => ({ ...prev, reason: event.target.value }))} data-testid="projects-archive-reason-select">
                <option value="">Select reason...</option>
                {archiveReasons.map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
              <label className="field-label">Type exact client name to confirm *</label>
              <Input value={archiveForm.confirm_name} onChange={(event) => setArchiveForm((prev) => ({ ...prev, confirm_name: event.target.value }))} placeholder={archiveModal.project?.client_name || "Client name"} data-testid="projects-archive-confirm-name-input" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setArchiveModal({ open: false, project: null })} data-testid="projects-archive-cancel-button">Cancel</Button>
              <Button onClick={confirmArchiveProject} disabled={!archiveConfirmMatched || isSubmitting} style={{ background: archiveConfirmMatched ? "#B91C1C" : "#FEE2E2", borderColor: archiveConfirmMatched ? "#B91C1C" : "#FECACA", color: archiveConfirmMatched ? "#fff" : "#EF9A9A" }} data-testid="projects-archive-confirm-button">
                {isSubmitting ? "Archiving..." : "Archive project"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </section>
    </AppShell>
  );
}
